// File: Home.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./Home.css";
import "./HomePanelZero.css";

import { supabase } from "../utils/supabaseClient";

import epicLogo from "../assets/ehr/epic.svg";
import oracleLogo from "../assets/ehr/oracle-health.svg";
import athenaLogo from "../assets/ehr/athenahealth.svg";
import meditechLogo from "../assets/ehr/meditech.svg";
import veradigmLogo from "../assets/ehr/veradigm.svg";
import eclinicalLogo from "../assets/ehr/eclinicalworks.svg";
import nextgenLogo from "../assets/ehr/nextgen.svg";
import greenwayLogo from "../assets/ehr/greenway.svg";
import fhirLogo from "../assets/ehr/fhir.svg";
import smartLogo from "../assets/ehr/smart.svg";

const EHR_LOGOS = [
  { src: epicLogo, name: "Epic" },
  { src: oracleLogo, name: "Oracle Health / Cerner" },
  { src: athenaLogo, name: "athenahealth" },
  { src: meditechLogo, name: "Meditech" },
  { src: veradigmLogo, name: "Veradigm" },
  { src: eclinicalLogo, name: "eClinicalWorks" },
  { src: nextgenLogo, name: "NextGen" },
  { src: greenwayLogo, name: "Greenway" },
  { src: fhirLogo, name: "FHIR R4" },
  { src: smartLogo, name: "SMART on FHIR" },
];

/* ──────────────────────────────────────────────────────────────
   Motion utilities
   ────────────────────────────────────────────────────────────── */
function usePrefersReducedMotion() {
  const [prefers, setPrefers] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setPrefers(mq.matches);
    setPrefers(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return prefers;
}

function useOnScreen(ref, { rootMargin = "0px", threshold = 0.25 } = {}) {
  const [onScreen, setOnScreen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setOnScreen(true),
      { rootMargin, threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, rootMargin, threshold]);
  return onScreen;
}

function useRevealOnScroll(ref, rootMargin = "0px 0px -10% 0px") {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && el.classList.add("is-visible")),
      { rootMargin, threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, rootMargin]);
}

function Reveal({ as: Tag = "div", className = "", children, margin }) {
  const r = useRef(null);
  useRevealOnScroll(r, margin);
  return <Tag ref={r} className={`reveal ${className}`}>{children}</Tag>;
}

/* Typewriter (from existing design) */
function TypewriterTitle({ prefix, accent, startDelay = 400, speed = 40, active = true }) {
  const reduce = usePrefersReducedMotion();
  const [i, setI] = useState(0);
  const [typingAccent, setTypingAccent] = useState(false);

  useEffect(() => {
    if (!active || reduce) return;
    const fullPrefix = prefix ?? "";
    const fullAccent = accent ?? "";
    const targetLen = typingAccent ? fullAccent.length : fullPrefix.length;
    if (i >= targetLen && typingAccent) return;

    const t = setTimeout(() => {
      if (!typingAccent) {
        if (i < fullPrefix.length) setI(i + 1);
        else { setTypingAccent(true); setI(0); }
      } else if (i < fullAccent.length) setI(i + 1);
    }, (i === 0 && !typingAccent) ? startDelay : speed);

    return () => clearTimeout(t);
  }, [active, reduce, i, typingAccent, prefix, accent, startDelay, speed]);

  if (reduce) {
    return (<>{prefix}<span className="accent-blue">{accent}</span></>);
  }
  if (!active) {
    return (
      <span className="typewriter" aria-hidden="true" style={{ opacity: 0 }}>
        {prefix}<span className="accent-blue">{accent}</span>
      </span>
    );
  }
  const fullPrefix = prefix ?? "";
  const fullAccent = accent ?? "";
  const showCursor = (!typingAccent && i < fullPrefix.length) || (typingAccent && i < fullAccent.length);
  return (
    <span className="typewriter" aria-live="polite">
      {typingAccent ? fullPrefix : fullPrefix.slice(0, i)}
      <span className="accent-blue">{typingAccent ? fullAccent.slice(0, i) : ""}</span>
      {showCursor && <span className="type-cursor" aria-hidden="true">|</span>}
    </span>
  );
}

/* ──────────────────────────────────────────────────────────────
   HERO VISUAL — "Denials → Appeals" live pipeline mockup
   ────────────────────────────────────────────────────────────── */
function HeroVisual() {
  return (
    <div className="hero-visual-wrap">
      <div className="hero-visual pz-hero-visual">
        <div className="visual-card visual-card-video pz-denial-card">
          <div className="visual-header">
            <div className="dot dot-red" />
            <div className="dot dot-yellow" />
            <div className="dot dot-green" />
            <span className="visual-title">Denial Queue — Live</span>
            <span className="visual-badge pz-badge-live"><span className="pz-pulse-dot" />42 new today</span>
          </div>

          <div className="pz-denial-row pz-row-processing">
            <div className="pz-code-chip">CO-197</div>
            <div className="pz-denial-meta">
              <div className="pz-denial-title">Prior auth not on file</div>
              <div className="pz-denial-sub">Patient 8821 · Anthem BCBS · $4,210</div>
            </div>
            <span className="pz-denial-status pz-status-working">
              <span className="pz-spin" /> Drafting…
            </span>
          </div>

          <div className="pz-denial-row">
            <div className="pz-code-chip pz-code-blue">CO-16</div>
            <div className="pz-denial-meta">
              <div className="pz-denial-title">Missing documentation</div>
              <div className="pz-denial-sub">Patient 8822 · UHC · $1,180</div>
            </div>
            <span className="pz-denial-status pz-status-done">Appeal sent</span>
          </div>

          <div className="pz-denial-row">
            <div className="pz-code-chip pz-code-purple">N115</div>
            <div className="pz-denial-meta">
              <div className="pz-denial-title">LCD policy mismatch</div>
              <div className="pz-denial-sub">Patient 8823 · Aetna · $2,840</div>
            </div>
            <span className="pz-denial-status pz-status-done">Overturned</span>
          </div>

          <div className="pz-denial-row">
            <div className="pz-code-chip">CO-50</div>
            <div className="pz-denial-meta">
              <div className="pz-denial-title">Not medically necessary</div>
              <div className="pz-denial-sub">Patient 8824 · Cigna · $9,620</div>
            </div>
            <span className="pz-denial-status pz-status-queued">Queued</span>
          </div>

          <div className="visual-footer">
            <div className="pulse" />
            <span>Scanning remit feed · 02:14 ago</span>
          </div>
        </div>

        <div className="visual-card visual-card-summary pz-appeal-card">
          <div className="summary-top">
            <div className="summary-icon pz-summary-icon">PZ</div>
            <div>
              <div className="summary-title">Appeal Draft — CO-197</div>
              <div className="summary-sub">GPT-5.5 · cited against LCD L35490</div>
            </div>
            <span className="summary-ready pz-summary-ready">Ready · 94%</span>
          </div>

          <div className="summary-lines pz-summary-lines">
            <div className="line w-90" />
            <div className="line w-80" />
            <div className="line w-70" />
            <div className="line w-85" />
          </div>

          <div className="summary-timestamps pz-evidence-chips">
            <div className="ts-chip pz-chip"><span className="pz-chip-dot" />Op note 3/18</div>
            <div className="ts-chip pz-chip"><span className="pz-chip-dot" />CPT 29827</div>
            <div className="ts-chip pz-chip"><span className="pz-chip-dot" />Medicare LCD</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   EHR INTEGRATIONS MARQUEE
   ────────────────────────────────────────────────────────────── */
function TrustedByMarquee() {
  const logos = EHR_LOGOS;
  let base = [...logos];
  while (base.length < 14) base = [...base, ...logos];
  const looped = [...base, ...base];

  return (
    <section id="integrations" className="trustedby" aria-label="EHR integrations">
      <div className="trustedby__inner">
        <div className="trustedby__title">
          Connects to every major EHR · FHIR R4, HL7 v2, X12 835/837
        </div>
        <div className="trustedby__marquee" role="list" aria-label="EHR systems PanelZero integrates with">
          <div className="trustedby__track">
            {looped.map((l, i) => (
              <div className="trustedby__item pz-ehr-item" role="listitem" key={`${l.name}-${i}`}>
                <img src={l.src} alt={`${l.name} logo`} loading="lazy" height="44" width="auto" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────
   ANIMATED DEMO — 4-step workflow that advances on a timer
   ────────────────────────────────────────────────────────────── */
const WORKFLOW_STEPS = [
  {
    key: "ingest",
    title: "Ingest denials and chart context",
    blurb: "835 from your clearinghouse for the denial, FHIR R4 from your EHR for the matching record. No VPN gymnastics.",
  },
  {
    key: "triage",
    title: "AI triages every denial",
    blurb: "GPT-5.5 reads the CARC/RARC, pulls op notes, and rates reversibility in under 3 seconds.",
  },
  {
    key: "draft",
    title: "Drafts a payer-specific appeal",
    blurb: "Pulls the payer's own policy language, attaches the chart, cites the LCD/NCD.",
  },
  {
    key: "submit",
    title: "Submits via fax, portal, or API",
    blurb: "One click (or fully auto). PanelZero tracks the clock and chases the follow-up.",
  },
];

function AnimatedWorkflow() {
  const wrapRef = useRef(null);
  const started = useOnScreen(wrapRef, { rootMargin: "0px 0px -30% 0px", threshold: 0.15 });
  const reduce = usePrefersReducedMotion();
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!started || reduce) return;
    const id = setInterval(() => setStep((s) => (s + 1) % WORKFLOW_STEPS.length), 2600);
    return () => clearInterval(id);
  }, [started, reduce]);

  // Typewriter-style appeal text, tied to the "draft" step
  const appealText =
    "Dear Anthem Appeals, This is a formal request to overturn denial CO-197 for patient 8821. The covered service (CPT 29827) meets LCD L35490 based on imaging dated 3/18 and the attached operative report. Per policy, prior authorization was not required for the expedited indication documented below…";
  const [typed, setTyped] = useState("");
  useEffect(() => {
    if (step !== 2) { setTyped(""); return; }
    if (reduce) { setTyped(appealText); return; }
    let i = 0;
    const id = setInterval(() => {
      i += 3;
      setTyped(appealText.slice(0, i));
      if (i >= appealText.length) clearInterval(id);
    }, 22);
    return () => clearInterval(id);
  }, [step, reduce]);

  return (
    <section className="pz-workflow" aria-label="How PanelZero works">
      <div className="pz-workflow__inner" ref={wrapRef}>
        <header className="pz-workflow__head">
          <span className="chip chip-live">Live demo</span>
          <h2 className="pz-workflow__title">
            A denial in. <span className="accent-blue">An appeal out.</span> In about 90 seconds.
          </h2>
          <p className="pz-workflow__sub">
            Watch PanelZero pick up a fresh denial, build the clinical case, and fire off the appeal — without the billing team touching a thing.
          </p>
        </header>

        <div className="pz-workflow__grid">
          {/* Left: step list */}
          <ol className="pz-steps">
            {WORKFLOW_STEPS.map((s, i) => (
              <li
                key={s.key}
                className={`pz-step ${i === step ? "is-active" : ""} ${i < step ? "is-done" : ""}`}
              >
                <div className="pz-step__num">
                  <span className="pz-step__ring" />
                  <span className="pz-step__n">{i + 1}</span>
                </div>
                <div className="pz-step__body">
                  <div className="pz-step__title">{s.title}</div>
                  <div className="pz-step__blurb">{s.blurb}</div>
                </div>
              </li>
            ))}
          </ol>

          {/* Right: animated panel */}
          <div className="pz-stage" aria-live="polite">
            <div className="pz-stage__chrome">
              <div className="pz-dot pz-dot-red" />
              <div className="pz-dot pz-dot-yellow" />
              <div className="pz-dot pz-dot-green" />
              <span className="pz-stage__title">
                app.panelzero.com / denials / 8821
              </span>
              <span className="pz-stage__badge">{WORKFLOW_STEPS[step].title}</span>
            </div>

            <div className="pz-stage__body">
              {/* Step 1: EHR ingest beam */}
              <div className={`pz-scene pz-scene-ingest ${step === 0 ? "is-on" : ""}`}>
                <div className="pz-ingest-rail">
                  <div className="pz-node pz-node-ehr">Epic</div>
                  <div className="pz-beam">
                    <span className="pz-beam-packet" />
                    <span className="pz-beam-packet pz-beam-packet--2" />
                    <span className="pz-beam-packet pz-beam-packet--3" />
                  </div>
                  <div className="pz-node pz-node-pz">PanelZero</div>
                </div>
                <div className="pz-ingest-meta">
                  <div>835 remit · 1,248 claims · 72 denied</div>
                  <div className="pz-ingest-count"><span className="pz-counter">72</span> denials ingested</div>
                </div>
              </div>

              {/* Step 2: AI triage rows */}
              <div className={`pz-scene pz-scene-triage ${step === 1 ? "is-on" : ""}`}>
                {["CO-197 · $4,210", "CO-16 · $1,180", "N115 · $2,840", "CO-50 · $9,620"].map((label, idx) => (
                  <div className={`pz-triage-row pz-triage-row--${idx}`} key={label}>
                    <span className="pz-triage-label">{label}</span>
                    <div className="pz-triage-bar">
                      <div className="pz-triage-bar__fill" style={{ "--pz-w": `${70 + idx * 7}%` }} />
                    </div>
                    <span className={`pz-triage-verdict pz-triage-verdict--${idx % 2 ? "low" : "high"}`}>
                      {idx % 2 ? "Low win%" : "High win%"}
                    </span>
                  </div>
                ))}
              </div>

              {/* Step 3: drafting */}
              <div className={`pz-scene pz-scene-draft ${step === 2 ? "is-on" : ""}`}>
                <div className="pz-draft-head">
                  <span className="pz-letter-icon">✉</span>
                  <div>
                    <div className="pz-draft-title">Appeal letter · Anthem BCBS</div>
                    <div className="pz-draft-sub">Denial CO-197 · Patient 8821 · $4,210</div>
                  </div>
                </div>
                <div className="pz-draft-body">
                  {typed}
                  <span className="pz-caret" aria-hidden="true">▎</span>
                </div>
                <div className="pz-draft-attach">
                  <span className="pz-attach">📎 op_note_3-18.pdf</span>
                  <span className="pz-attach">📎 imaging_summary.pdf</span>
                  <span className="pz-attach">📎 LCD_L35490.pdf</span>
                </div>
              </div>

              {/* Step 4: submit */}
              <div className={`pz-scene pz-scene-submit ${step === 3 ? "is-on" : ""}`}>
                <div className="pz-submit-grid">
                  <div className="pz-submit-tile"><span className="pz-submit-ico">📠</span><div><div className="pz-submit-title">Fax · Anthem</div><div className="pz-submit-sub">Page 1 of 9 · delivered</div></div></div>
                  <div className="pz-submit-tile"><span className="pz-submit-ico">🌐</span><div><div className="pz-submit-title">Availity Portal</div><div className="pz-submit-sub">Case #A-48192</div></div></div>
                  <div className="pz-submit-tile"><span className="pz-submit-ico">🔁</span><div><div className="pz-submit-title">Follow-up scheduled</div><div className="pz-submit-sub">In 14 days</div></div></div>
                </div>
                <div className="pz-submit-stamp">
                  <span className="pz-check">✓</span>
                  <div>
                    <div className="pz-submit-done">Appeal submitted</div>
                    <div className="pz-submit-done-sub">$4,210 · expected turnaround 21 days</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pz-stage__progress">
              <div className="pz-stage__bar" style={{ width: `${((step + 1) / WORKFLOW_STEPS.length) * 100}%` }} />
            </div>
          </div>
        </div>

        <div className="pz-feature-band" role="list">
          <div className="pz-feature" role="listitem">
            <h3>3.1× faster</h3>
            <p>Average appeal turnaround drops from days to under an hour.</p>
          </div>
          <div className="pz-feature" role="listitem">
            <h3>+38% overturn rate</h3>
            <p>Payer-specific language and cited policy numbers change the win rate.</p>
          </div>
          <div className="pz-feature" role="listitem">
            <h3>0 IT lift</h3>
            <p>FHIR R4 to your EHR + 835 SFTP from your clearinghouse. Live in days, not quarters.</p>
          </div>
          <div className="pz-feature" role="listitem">
            <h3>HIPAA-aligned</h3>
            <p>Tenant-isolated Postgres, audit logs, BAA on signature.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────
   CTA / Contact form (Supabase, falls back to mailto)
   ────────────────────────────────────────────────────────────── */
function CTAForm() {
  const [org, setOrg] = useState("");
  const [website, setWebsite] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFeedback(null);
    if (!org || !email || !name) {
      setLoading(false);
      alert("Please fill in the required fields (Organization, Name, Email).");
      return;
    }
    try {
      const client = supabase;
      if (!client) throw new Error("supabase missing");
      const { error } = await client.from("inbound_requests").insert([
        { organization: org, website, contact_name: name, contact_email: email, notes },
      ]);
      if (error) throw error;
      setFeedback("success");
      setOrg(""); setWebsite(""); setName(""); setEmail(""); setNotes("");
    } catch (err) {
      // Fallback: mailto so the form is always useful even without Supabase keys
      const body = encodeURIComponent(
        `Organization: ${org}\nWebsite: ${website}\nName: ${name}\nEmail: ${email}\nNotes: ${notes}`
      );
      window.location.href = `mailto:adcheng4@illinois.edu?subject=PanelZero%20demo%20request&body=${body}`;
      setFeedback("success");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="setup" aria-label="Request a PanelZero demo">
      <div className="setup__inner">
        <div className="setup__grid">
          <div className="setup__left">
            <span className="chip chip-live">For revenue cycle leaders</span>
            <h2 className="setup__title">
              Every denial turned into a drafted appeal — <span className="accent-blue">before your team opens the next chart</span>.
            </h2>
            <p className="setup__sub">
              PanelZero pulls denials directly from your EHR, drafts payer-specific appeals with clinical evidence,
              and submits them on your behalf. Your staff reviews; we do the rest.
            </p>
            <ul className="setup__bullets">
              <li><strong>Right-sized for you:</strong> from independent practices to multi-hospital systems.</li>
              <li><strong>Launch in days:</strong> FHIR R4 against your EHR for the clinical pull, 835 SFTP from your clearinghouse for the denial feed.</li>
              <li><strong>No workflow change:</strong> review queue lives inside your existing dashboard or ours.</li>
              <li><strong>Measurable ROI:</strong> we model your recoverable revenue in the first demo call.</li>
            </ul>
          </div>

          <div className="setup__right">
            <form className="setup__form" onSubmit={handleSubmit}>
              <div className="setup__row">
                <label>
                  <span>Hospital / practice name</span>
                  <input value={org} onChange={(e) => setOrg(e.target.value)} placeholder="Your organization" required />
                </label>
              </div>
              <div className="setup__row">
                <label>
                  <span>Website</span>
                  <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://yourpractice.com" />
                </label>
              </div>
              <div className="setup__row setup__row--2">
                <label>
                  <span>Contact name</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required />
                </label>
                <label>
                  <span>Work email</span>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourpractice.com" required />
                </label>
              </div>
              <div className="setup__row">
                <label>
                  <span>EHR + top denial reasons (optional)</span>
                  <textarea
                    rows={4}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. Epic and ~$1.8M/mo, or athenahealth and ~$120K/mo. Top reasons: prior auth and medical necessity."
                  />
                </label>
              </div>
              <div className="setup__submit">
                <button className="btn-cta is-lg" type="submit" disabled={loading || feedback === "success"}>
                  {loading ? "Sending…" : feedback === "success" ? "Sent! We'll be in touch." : "Request my demo"}
                </button>
              </div>
              {feedback === "error" && (
                <div style={{ marginTop: "1rem", color: "#ef4444", fontSize: "0.9rem", fontWeight: 500 }}>
                  {/* Restore when hello@panelzero.com is live:
                  Something went wrong. Email us at hello@panelzero.com. */}
                  Something went wrong. Please try again in a moment.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────
   FAQ
   ────────────────────────────────────────────────────────────── */
function FAQSection() {
  const faqs = [
    {
      q: "What does PanelZero actually do?",
      a: "PanelZero is an AI layer that sits next to your revenue cycle team. It watches your 835 remits (and FHIR ClaimResponse where your payers expose it) for denials, reads the reason codes, pulls the matching clinical evidence from your EHR over FHIR R4, drafts a payer-specific appeal, and submits it — by fax, portal, or API — then tracks the timer until the payer responds.",
    },
    {
      q: "Which EHRs do you integrate with?",
      a: "On the clinical side, anything that speaks FHIR R4 — Epic, Oracle Health (Cerner), Meditech, athenahealth, Veradigm (Allscripts), eClinicalWorks, NextGen, Greenway. We support SMART on FHIR launch for embedded use inside Epic. Denials come in via 835 SFTP from your clearinghouse (Availity, Change/Optum, Waystar, etc.), and via FHIR ClaimResponse from payers that expose it.",
    },
    {
      q: "Is a human still in the loop?",
      a: "Yes, by default. Every appeal is drafted automatically and parked for review in either your existing queue or PanelZero's console. Once a customer trusts a specific denial reason, they can flip it to fully auto-submit.",
    },
    {
      q: "How fast can we go live?",
      a: "Most teams — from independent practices to hospital systems — are in pilot within 1–2 weeks. A FHIR R4 read connection to your EHR, an 835 SFTP feed from your clearinghouse, and a BAA is all we need to start running analysis. Full auto-submission typically adds another 2–4 weeks of payer setup.",
    },
    {
      q: "What about PHI, HIPAA, and SOC 2?",
      a: "PanelZero is built multi-tenant with Postgres row-level security, tenant-scoped encryption keys, and audit trails on every action. We sign a BAA, host in HIPAA-eligible Azure regions, and are mid-SOC 2 Type II (report available under NDA).",
    },
    {
      q: "How does pricing work?",
      a: "A small platform fee plus a performance fee on recovered revenue. If we don't recover more than we cost, you don't pay the performance fee. We'll model expected ROI from a sample of your denial data on the first call.",
    },
  ];

  const [openIndex, setOpenIndex] = useState(-1);

  return (
    <section id="faq" className="faq" aria-label="Frequently asked questions">
      <header className="faq__head">
        <span className="chip chip-live" aria-hidden="true">FAQ</span>
        <h2 className="faq__title">Questions revenue cycle leaders ask us first</h2>
        {/* Restore when hello@panelzero.com is live:
        <p className="faq__sub">Integrations, security, timelines, pricing. If we missed one, email hello@panelzero.com.</p>
        */}
        <p className="faq__sub">Integrations, security, timelines, pricing. If we missed one, reach out through the request-demo form.</p>
        <div className="faq__rule" aria-hidden="true" />
      </header>

      <div className="faq__list" role="list">
        {faqs.map((item, i) => {
          const isOpen = i === openIndex;
          const btnId = `faq-btn-${i}`;
          const panelId = `faq-panel-${i}`;
          return (
            <div className={"faq__item" + (isOpen ? " is-open" : "")} role="listitem" key={i}>
              <button
                id={btnId}
                className="faq__question"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenIndex(isOpen ? -1 : i)}
              >
                <span className="faq__qtext">{item.q}</span>
                <span className="faq__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path className="line-vert" d="M12 5v14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path className="line-horz" d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </span>
              </button>
              <div id={panelId} className="faq__answer" role="region" aria-labelledby={btnId}>
                <p>{item.a}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────
   DEMO SECTION wrapper (keeps the old structure + adds AnimatedWorkflow)
   ────────────────────────────────────────────────────────────── */
function PZDemoSection() {
  const headRef = useRef(null);
  const startTyping = useOnScreen(headRef, { rootMargin: "0px 0px -35% 0px", threshold: 0.2 });

  return (
    <section className="bw-showcase" aria-label="PanelZero product tour">
      <div className="bw-showcase__inner">
        <header className="bw-showcase__head" ref={headRef}>
          <div className="bw-head-left">
            <span className="chip chip-live" aria-hidden="true">Product tour</span>
            <h2 className="bw-head-title">
              <TypewriterTitle prefix="See it in action: " accent="PanelZero console" active={startTyping} />
            </h2>
            <p className="bw-head-sub">A real denial, moved through the pipeline — in about 10 seconds.</p>
          </div>
          <div className="bw-head-right">
            <Link className="btn-cta is-lg btn-cta--ghostdemo" to="/contact" aria-label="Request a PanelZero demo">
              <span>Get my ROI estimate</span>
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path d="M5 12h12M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>
        </header>

        <AnimatedWorkflow />
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────
   PAGE
   ────────────────────────────────────────────────────────────── */
export default function Home(props) {
  const {
    heroExtra = "0px",
    subtitleOffset = "10px",
    shineGradientOpacity = 0.76,
    shineAnimOpacity = 0.5,
    shineWidth = "60%",
    shineBlur = "14px",
    dashboardMaxWidth = "980px",
  } = props;

  return (
    <div
      className="home-page pz-home"
      style={{
        "--hero-extra-space": heroExtra,
        "--sub-offset": subtitleOffset,
        "--shine-gradient-opacity": shineGradientOpacity,
        "--shine-anim-opacity": shineAnimOpacity,
        "--shine-width": shineWidth,
        "--shine-blur": shineBlur,
        "--dashboard-max-width": dashboardMaxWidth,
        "--showcase-offset-y": "32px",
        "--trustedby-offset-y": "28px",
      }}
    >
      {/* HERO */}
      <section className="about-hero home-hero">
        <div className="about-hero-inner">
          <div className="about-hero-text">
            <div className="about-kicker">AI denial management for hospitals & private practices</div>
            <h1 className="about-title">
              Denial in. <span className="pz-accent">Appeal out.</span>
              <br />
              Zero rework.
            </h1>

            <p className="about-subtitle">
              PanelZero pulls denied claims straight from your EHR, drafts payer-specific appeals with clinical evidence,
              and submits them by fax, portal, or API — so your team stops chasing paper and starts recovering revenue.
            </p>

            <div className="about-cta-row">
              <Link to="/contact" className="btn-primary">Request demo</Link>
              <a href="#workflow" className="btn-ghost">See it work →</a>
            </div>

            <div className="about-metrics">
              <div className="metric">
                <div className="metric-value">$4.2M</div>
                <div className="metric-label">recoverable revenue · pilot deployment, 90 days</div>
              </div>
              <div className="metric">
                <div className="metric-value">3.1×</div>
                <div className="metric-label">faster appeal turnaround</div>
              </div>
              <div className="metric">
                <div className="metric-value">+38%</div>
                <div className="metric-label">denial overturn rate lift</div>
              </div>
            </div>
          </div>

          <div className="about-hero-media">
            <HeroVisual />
          </div>
        </div>
      </section>

      {/* EHR marquee */}
      <TrustedByMarquee />

      {/* Animated workflow demo */}
      <div id="workflow" />
      <Reveal as="div" margin="0px 0px -12% 0px"><PZDemoSection /></Reveal>

      {/* Demo request */}
      <Reveal as="div"><CTAForm /></Reveal>

      {/* FAQ */}
      <Reveal as="div"><FAQSection /></Reveal>
    </div>
  );
}
